import { __awaiter } from 'tslib';
import { ErrorType, IMEvent, InitResult, InfoKeys } from '../consts.js';
import { BaseError } from '../errors/BaseError.js';
import { Message } from '../model/Message.js';
import { CommandProcessor } from './processor/CommandProcessor.js';
import { ConversationLoader } from './processor/ConversationLoader.js';
import { TextMessageProcessor } from './processor/TextMessageProcessor.js';
import { UpdateExtProcessor } from './processor/UpdateExtProcessor.js';
import { Logger } from '../metrics/logger.js';
import { im_proto } from '../../proto/index.js';
import { ContextClassBase, ServiceIdentity } from './RuntimeContext.js';

class MessageManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
        this.messages = new Map();
        this.processors = [
            new ConversationLoader(this.ctx),
            new CommandProcessor(this.ctx),
            new UpdateExtProcessor(this.ctx),
            new TextMessageProcessor(this.ctx)
        ];
    }
    applyLocal(local) {
        local.forEach(m => {
            if (!this.messages.has(m.conversationId)) {
                this.messages.set(m.conversationId, new Map());
            }
            const convMap = this.messages.get(m.conversationId);
            convMap.set(m.clientId, m);
        });
    }
    clearAll() {
        this.messages.clear();
    }
    get(conversationId, clientId) {
        const msg = this.getRaw(conversationId, clientId);
        if (!msg) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.MessageNotExist,
                msg: `message ${clientId} @ ${conversationId} not exist in local`,
                sender: this
            });
        }
        return msg;
    }
    getRaw(conversationId, clientId) {
        return this.getConversationMap(conversationId).get(clientId);
    }
    getRawMap() {
        return this.messages;
    }
    getByServerIdRaw(conversationId, serverId) {
        const msgs = this.getConversationMap(conversationId);
        const msg = Array.from(msgs.values()).filter(m => m.serverId === serverId);
        if (msg.length === 0) {
            return undefined;
        }
        return this.get(conversationId, msg[0].clientId);
    }
    getByServerId(conversationId, serverId) {
        const msgs = this.getConversationMap(conversationId);
        const msg = Array.from(msgs.values()).filter(m => m.serverId === serverId);
        if (msg.length === 0) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.MessageNotExist,
                msg: `message ${serverId} @ ${conversationId} not exist in local`,
                sender: this
            });
        }
        return this.get(conversationId, msg[0].clientId);
    }
    getList(conversationId) {
        const map = this.getConversationMap(conversationId);
        return Array.from(map.values()).sort(MessageManager.messageComparator); // TODO: avoid sort, try append only and assume it is ordered
    }
    upsert(message) {
        var _a;
        if (message.isSpecialMessage) {
            Logger.ctxWarn(this.ctx, 'unexpected special message to upsert:', message);
            return;
        }
        const map = this.getConversationMap(message.conversationId);
        // 本地存储消息超过上限就清除最久的 1/5 数量的消息，防止应用挂后台占用过多内存
        if (this.ctx.option.maxMessageCount) {
            const maxMessageCount = Math.max(200, Math.min(this.ctx.option.maxMessageCount, 2000));
            if (map.size >= maxMessageCount) {
                let count = ~~(maxMessageCount / 5);
                for (const msgId of map.keys()) {
                    if (count <= 0) {
                        break;
                    }
                    map.delete(msgId);
                    count--;
                }
            }
        }
        const existMsg = map.get(message.clientId);
        if (existMsg && existMsg.type === message.type) {
            // callback 异常时去除本地相关的字段
            const toRemoveFields = ['referenceInfo'];
            toRemoveFields.forEach((item) => {
                if (message[item] === undefined) {
                    delete existMsg[item];
                }
            });
            message = existMsg.merge(message); // 合并消息
        }
        else if (existMsg && existMsg.type !== message.type) {
            Logger.ctxWarn(this.ctx, 'try to merge different msg:', existMsg, message);
        }
        map.set(message.clientId, message);
        void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.upsertMessage(message));
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessageUpsert, this, message);
        // 频率太高了, 初始化阶段不触发这个
        if (this.ctx.initResult === InitResult.Succeeded) {
            this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
            const conv = this.getContext().resolve(ServiceIdentity.ConversationManager).getRaw(message.conversationId);
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, conv);
            if (existMsg !== undefined) { // 更新已存在的消息, 刷新一次未读数
                conv === null || conv === void 0 ? void 0 : conv.forceRefreshUnreadCount();
            }
        }
    }
    getConversationMap(conversationId) {
        let arr = this.messages.get(conversationId);
        if (!arr) {
            arr = new Map();
            this.messages.set(conversationId, arr);
        }
        return arr;
    }
    // message processor
    processNewMessage(message, source) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!message.ext) {
                message.ext = {};
            }
            message.source = source;
            if (message.type >= 0) { // allow type === 0
                return this.processMessage(message);
            }
            else {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.UnknownMessageType,
                    msg: `unknown message type: ${message.type} for msg:${message.clientId}`,
                    sender: this
                });
            }
        });
    }
    processNewMessagesFromPull(ms, source, logid) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const msgs = ms.map(m => Message.fromServerMessage(this.ctx, m, logid));
            const conMap = new Map();
            for (const m of msgs) {
                if (!m.ext) {
                    m.ext = {};
                }
                const indexInConversation = (_a = conMap.get(m.conversationShortId)) === null || _a === void 0 ? void 0 : _a.indexInConversation;
                if (!indexInConversation || m.indexInConversation.lt(indexInConversation)) {
                    conMap.set(m.conversationShortId, m);
                }
                yield this.processNewMessage(m, source);
            }
            return { msgs, conMap };
        });
    }
    delete(conversationId, serverId) {
        var _a, _b, _c;
        const msgs = this.getConversationMap(conversationId);
        const target = Array.from(msgs.values()).filter(m => m.serverId === serverId);
        if (target.length === 0) {
            Logger.ctxDebug(this.ctx, `delete not exist msg: ${conversationId}::${serverId}`);
            return;
        }
        msgs.delete(target[0].clientId);
        void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.deleteMessage(target[0]));
        // update ref message
        const allMessages = this.getList(conversationId);
        for (const m of allMessages) {
            if (((_c = (_b = m.referenceInfo) === null || _b === void 0 ? void 0 : _b.referenced_message_id) === null || _c === void 0 ? void 0 : _c.toString()) === serverId) {
                m.referenceInfo.referenced_message_status = im_proto.MessageStatus.DELETED;
                this.upsert(m);
            }
        }
        const conv = this.getContext().resolve(ServiceIdentity.ConversationManager).get(conversationId);
        this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, conv);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessageUpsert, this, target[0]);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessageDelete, this, target[0]);
    }
    markRecalled(conversationId, serverId, logid) {
        var _a, _b;
        const msg = this.getByServerId(conversationId, serverId);
        if (!msg.ext) {
            msg.ext = {};
        }
        msg.ext[InfoKeys.IsRecalled] = 'true';
        if (logid && this.ctx.option.debug) {
            msg.ext[InfoKeys.LocalLogId] = logid;
        }
        this.upsert(msg);
        // update ref messages
        const allMessages = this.getList(conversationId);
        for (const m of allMessages) {
            if (((_b = (_a = m.referenceInfo) === null || _a === void 0 ? void 0 : _a.referenced_message_id) === null || _b === void 0 ? void 0 : _b.toString()) === serverId) {
                m.referenceInfo.referenced_message_status = im_proto.MessageStatus.RECALLED;
                this.upsert(m);
            }
        }
        const conv = this.getContext().resolve(ServiceIdentity.ConversationManager).get(conversationId);
        this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, conv);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessageUpsert, this, msg);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessageRecall, this, msg);
    }
    clearConversation(conversationId) {
        var _a;
        const conv = this.getContext().resolve(ServiceIdentity.ConversationManager).get(conversationId);
        this.messages.set(conversationId, new Map());
        void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.clearConversation(conv));
        this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, conv);
    }
    injectProcessor(processor) {
        this.processors.push(processor);
    }
    processMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            let item = {
                needContinue: true,
                data: message
            };
            for (const processor of this.processors) {
                item = yield processor.process(item);
                if (!item.needContinue) {
                    break;
                }
            }
            return item.data;
        });
    }
    dispose() {
        this.messages.clear();
        this.processors = [];
    }
}
MessageManager.messageComparator = (a, b) => a.orderInConversation.gt(b.orderInConversation) ? 1 : -1;

export { MessageManager };
//# sourceMappingURL=MessageManager.js.map
